import { Component, HostBinding, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { AppService } from '../../../services/app.service';
import { MessageSeverity } from '../../../enums/message-severity';
import { SettingsService } from '../../../services/settings/settings.service';
import { HttpErrorResponse } from '@angular/common/http';
import { UserSession } from '../../../interfaces/user-session';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { Subject, takeUntil } from 'rxjs';
import { ActionButton } from '../../../models/app/action-button';
import { AvatarPrimitive } from '../../../models/app/avatar-primitive';
import { Person } from '../../../models/data/app-core/person';
import { PersonService } from '../../../services/persons/person.service';
import { PersonSearchRequest } from '../../../models/net/request/person-search-request';
import { PersonSearchResult } from '../../../models/net/result/person-search-result';
import { ReadResult } from '../../../models/net/result/crud/read-result';
import { PersonEnvelope } from '../../../models/net/envelope/person-envelope';
import { OrganizationalUnit } from '../../../models/data/app-core/organizational-unit';
import { OrganizationalUnitPerson } from '../../../models/data/app-core/organizational-unit-person';
import { ChipSelectorComponent } from '../../fragments/chip-selector/chip-selector.component';
import { PersonPreferencesComponent } from '../../fragments/person-preferences/person-preferences.component';

@Component({
    selector: 'app-profile',
    templateUrl: './profile.component.html',
    styleUrls: ['./profile.component.css', '../../../app.component.css']
})

export class ProfileComponent implements OnInit, OnDestroy {

    public dataSource: any = null;
    public title: string = "Session:";
    public subtitle: string = "You are logged in with user credentials {User.UID} with roles {roles.map((role) => role.UID)}";
    public availableOrganizationalUnits: AvatarPrimitive[] = [];
    public selectedOrganizationalUnitIds: number[] = [];
    public currentPerson: Person | undefined;
    public personEnvelope: PersonEnvelope | null = null;

    showDropdown = false;

    @HostBinding("class.component-flex-vertical-fill") addClass = true;

    public avatarUri: string = "";

    @ViewChild(MatSort) sort = new MatSort();
    @ViewChild(MatPaginator) paginator: any;
    @ViewChild("organizationalUnitChipSelector") organizationalUnitChipSelector!: ChipSelectorComponent;
    @ViewChild("#personPreferences") personPreferences!: PersonPreferencesComponent;

    private destroy$ = new Subject<void>();

    public constructor(public appService: AppService, private personService: PersonService, private settingsService: SettingsService) {

        const userSessionString: string | null = this.appService.getUserSession();

        if (userSessionString) {

            const userSession: UserSession = JSON.parse(userSessionString);

            let personSearchRequest: PersonSearchRequest = new PersonSearchRequest();

            personSearchRequest.BasicSearchString.SearchString = userSession.userUID;
            personSearchRequest.BasicSearchString.IsWildPrefix = false;
            personSearchRequest.BasicSearchString.IsWildSuffix = false;

            this.personService.search(personSearchRequest).subscribe((r: PersonSearchResult[]) => {

                if (r) {

                    if (r.length > 0) {

                        let personId: number | undefined;

                        if (r.length > 1) {

                            personId = r.find((p: PersonSearchResult) => p.UserUID === userSession.userUID)?.PersonId;

                        } else {

                            personId = r[0].PersonId;
                        }

                        if (personId) {

                            // go get the person envelope which contains the organizational unit membership details
                            this.personService.read(personId).subscribe((readResult: ReadResult | null) => {

                                if (readResult) {

                                    this.personEnvelope = readResult.ReadEnvelope as PersonEnvelope;
                                    //this.personPreferences.setPersonEnvelope(personEnvelope);
                                    this.currentPerson = (readResult.ReadEnvelope as PersonEnvelope).Document.Item;

                                    this.availableOrganizationalUnits = (readResult.ReadEnvelope as PersonEnvelope).OrganizationalUnitsPool.map((i: OrganizationalUnit) => {
                                        let result = new AvatarPrimitive();
                                        result.Id = i.OrganizationalUnitId;
                                        result.Name = i.Name;
                                        result.AvatarBlobPath = i.AvatarBlobPath ?? this.appService.defaultImageUri;
                                        return result;
                                    });

                                    this.selectedOrganizationalUnitIds = (readResult.ReadEnvelope as PersonEnvelope).Document.OrganizationalUnits.map((i: OrganizationalUnitPerson) => i.OrganizationalUnitId);

                                    // setup the title and subtitle
                                    this.title = "Welcome " + this.currentPerson.FirstName;
                                    this.subtitle = "You are logged in with user credentials " + userSession.userUID + " as " + this.currentPerson.FirstName + " " + this.currentPerson.LastName;
                                }
                            });
                        }
                    }
                }
            });
        }
    }

    public ngOnInit(): void {

        // setup actions
        let actionButtons: ActionButton[] = [];

        actionButtons.push(new ActionButton("APP FEED", "", "", ""));

        this.appService.setActionsDataSource(actionButtons);
    }

    public ngOnChanges() {


    }

    public ngAfterViewInit(): void {

        this.organizationalUnitChipSelector.itemAdded.subscribe((item: AvatarPrimitive) => {

            this.selectedOrganizationalUnitIds.push(item.Id);
            //this.addOrganizationalUnitToPerson(item.Id);
        });

        this.organizationalUnitChipSelector.itemRemoved.subscribe((item: AvatarPrimitive) => {

            this.selectedOrganizationalUnitIds.splice(this.selectedOrganizationalUnitIds.indexOf(item.Id), 1);
            //this.removeOrganizationalUnitFromPerson(item.Id);
        });
    }

    public showDialog = async (isShowAppFeed: boolean = false): Promise<void> => {

        this.appService.IsAppBusy = true;

        await this.delay(1800);

        this.appService.IsAppBusy = false;

        let message = "Demonstration to check the functionality of app dialogs";

        if (isShowAppFeed) {

            message += " This is a demo error message (with AppFeed) after showing the loader"

            this.appService.showDialog(MessageSeverity.Error, "Demo", message, "OH CRAP !!", "", true, true);

        } else {

            this.appService.showDialog(MessageSeverity.Success, "Demo", message, "SOUNDS GOOD !", "");
        }
    }

    public getSettings = async (): Promise<void> => {

        this.settingsService.getSettings().subscribe((settings) => {

            if (settings) {

                if (settings instanceof HttpErrorResponse) {

                    this.appService.showDialog(MessageSeverity.Error, "Settings result", "Unable to get settings :(", "DANG !", "", true, true);

                } else {

                    this.appService.showDialog(MessageSeverity.Info, "Settings result", "GotSettings where JSON.stringify(settings) is " + JSON.stringify(settings), "GREAT !", "", true, true);
                }
            }
        });
    }

    private delay = (ms: number): Promise<void> => {

        let result: Promise<void> = new Promise((resolve) => setTimeout(resolve, ms));

        return result;
    }

    public ngOnDestroy() {

        this.appService.setActionsDataSource([]);
        this.destroy$.next();
        this.destroy$.complete();
    }
}
